import React, { useEffect, useState } from 'react';
import { getCurrentUser, fetchAuthSession, signOut } from 'aws-amplify/auth';

import { useApolloClientsContext } from '../../providers/ApolloClientsProvider';
import { ConfigContext } from '../../providers/ConfigContext';
import { Configuration } from '../../providers/ConfigContext/types';
import { useRoleGuardContext } from '../../providers/RoleGuardContext';
import { UserApplicationRole } from '../../constants';
import type { HTMLAttributeAnchorTarget } from 'react';
import { useAppDrawerContext } from '../AppDrawerContext';
import { Logo } from '../Logo';
import { UserAgreementView } from './components/UserAgreementView';

import {
  Container,
  Header,
  HeaderContent,
  CloseButton,
  CloseIcon,
  Content,
  NavSection,
  UserSection,
  Username,
  LogoutButton,
  NavItem,
  StyledArrowIcon,
} from './styles';

const BASE_URL = `${window.location.protocol}//${window.location.host}`;

const NAV_ITEMS: NavItemTemplate[] = [
  { key: '', label: 'Home', hrefFn: () => `${BASE_URL}/` },
  {
    key: 'community-admin',
    label: 'Community admin',
    hrefFn: () => `${BASE_URL}/community-admin/`,
  },
  {
    key: 'organization-admin',
    label: 'Organization admin',
    hrefFn: () => `${BASE_URL}/organization-admin/`,
  },
  {
    key: 'port-dues',
    label: 'Port dues',
    hrefFn: () => `${BASE_URL}/port-dues/`,
  },
  {
    key: 'visit-notification',
    label: 'Visit notification',
    hrefFn: () => `${BASE_URL}/visit-notification/`,
  },
  {
    key: 'port-call',
    label: 'Port call',
    hrefFn: () => `${BASE_URL}/port-call/`,
  },
  {
    key: 'visit-view',
    label: 'Visit view',
    hrefFn: () => `${BASE_URL}/visit-view/`,
  },
  {
    key: 'hydrometeo',
    label: 'Hydrometeo',
    hrefFn: () => `${BASE_URL}/hydrometeo/`,
  },
  {
    key: 'configuration',
    label: 'Configuration',
    hrefFn: () => `${BASE_URL}/configuration/`,
  },
  { key: 'mocker', label: 'Mocker', hrefFn: () => `${BASE_URL}/mocker/` },
  {
    key: 'signaling',
    label: 'Signaling',
    hrefFn: () => `${BASE_URL}/signaling/`,
  },
  {
    key: 'map',
    label: 'Map',
    target: '_blank',
    hrefFn: (config) => config.portXchange || '',
  },
];

export const NavigationDrawer = () => {
  const { close } = useAppDrawerContext();
  const config = ConfigContext.useContainer();
  const { roles } = useRoleGuardContext();
  const { clients } = useApolloClientsContext();

  const [username, setUsername] = useState<string>('');

  const navItems = NAV_ITEMS.filter(
    (item) => !item.key || roles.includes(item.key),
  );

  const agreementsEnabled = !!clients.agreements;

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await getCurrentUser();
        let displayName = user.username;
        try {
          const session = await fetchAuthSession();
          const email = session.tokens?.idToken?.payload?.email as string | undefined;
          displayName = email ?? user.username;
        } catch (sessionError) {
          console.error('[NavigationDrawer] fetchAuthSession failed:', sessionError);
        }
        setUsername(displayName);
      } catch {
        setUsername('');
      }
    };

    loadUser();
  }, []);

  const handleLogout = async () => {
    try {
      // Clear local Amplify tokens. Do NOT use { global: true } here —
      // the app uses Cognito Hosted UI / OAuth tokens, and GlobalSignOut
      // only works with native SRP-auth tokens, causing a 400 from Cognito.
      await signOut();
    } catch (error) {
      console.error('Error clearing local session', error);
    }
    // Always redirect to the Cognito hosted logout endpoint to invalidate the
    // OAuth session server-side. Without this, the Cognito session cookie
    // persists and the user can regain access without re-authenticating.
    const { domain, clientId, redirectSignOut, cognitoLogoutUrl } =
      config.cognito;
    const logoutUrl =
      cognitoLogoutUrl ||
      `${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
        redirectSignOut,
      )}`;
    window.location.href = logoutUrl;
  };

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Logo />
        </HeaderContent>
        <CloseButton onClick={close}>
          <CloseIcon />
        </CloseButton>
      </Header>

      <Content>
        <NavSection>
          {navItems.map((item) => {
            const href = item.hrefFn(config);

            return (
              <NavItem
                key={item.key}
                href={href}
                target={item.target}
                selected={
                  item.key === ''
                    ? window.location.pathname === '/'
                    : window.location.href.startsWith(href)
                }
              >
                {item.label}
                <StyledArrowIcon />
              </NavItem>
            );
          })}
        </NavSection>

        <UserSection>
          <Username>{username}</Username>
          <LogoutButton onClick={handleLogout} secondary>
            Log out
          </LogoutButton>
        </UserSection>

        {agreementsEnabled && <UserAgreementView onToggle={close} />}
      </Content>
    </Container>
  );
};

interface NavItemTemplate {
  key: UserApplicationRole | '';
  label: string;
  target?: HTMLAttributeAnchorTarget;
  hrefFn: (config: Configuration) => string;
}
